'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { CompanyPreviewPanel } from '@/new_components/searchresults/CompanyPreviewPanel';
import { useCompanyPreview } from '@/new_components/searchresults/useCompanyPreview';
import { searchParamsWithoutPreview } from '@/lib/search-filters';
import CompanyResultsTable from '@/new_components/searchresults/CompanyResultsTable';
import { AiSearchBanner } from '@/new_components/searchresults/AiSearchBanner';
import { AiSearchLoading } from '@/new_components/searchresults/AiSearchLoading';
import { aiSearchKey } from '@/lib/ai-search';
import { getAiSearchProgress, resetAiSearchProgress, useAiSearchProgress } from '@/lib/ai-search-progress';
import {
  CompanySearchResponse,
  CompanySearchResult,
  searchCompanies,
} from '@/new_components/searchresults/companySearchService';
import CompanySearchSidebar, {
  type FilterParams,
} from '@/new_components/searchresults/CompanySearchSidebar';
import {
  activeFilterCount,
  activeFilterValues,
  hasAnyFilter,
} from '@/new_components/searchresults/activeFilters';
import { filterLocationsForCountry } from '@/new_components/searchresults/searchConstants';
import { SaveSearchDialog } from '@/new_components/saved-searches/SaveSearchDialog';
import {
  useMarkOpened,
  useSavedSearches,
} from '@/hooks/use-saved-searches';
import {
  canonicalFilters,
  buildBackendFilters,
  normalizeFilterParams,
  parseMetricURLBounds,
  normalizeSearchSort,
  sanitizeFinancialYear,
} from '@/lib/search-filters';
import { filterParamsToParsed } from '@/lib/seo-lists/filter-adapter';
import { ActiveFilterBar } from '@/new_components/searchresults/ActiveFilterBar';
import { useTranslation } from 'react-i18next';
import {
  trackSearchPagination,
  trackSearchPerformed,
  trackSearchSortChange,
} from '@/lib/analytics';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { isAdmin, isPro } from '@/lib/permissions';
import { type FilterTier, tierForSession } from '@/lib/search-gating';
import { useLocale } from '@/hooks/use-locale';
import { useRecordRecentSearch } from '@/hooks/use-recent-activity';
import { useSession } from '@/hooks/use-session';
import { interactionState } from '@/components/page-view-logger';

interface NaceCode {
  code: string;
  description: string;
  level: number;
}

interface SearchPageClientProps {
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  searchResponse: CompanySearchResponse;
  /**
   * True when the server intentionally skipped SSR for the default (no-filter)
   * search and returned an empty response. Lets the client distinguish that
   * placeholder-empty from a real search that genuinely returned 0 results, so
   * a 0-result search correctly clears the previous results instead of keeping
   * them on screen.
   */
  searchSkipped: boolean;
  initialOffset: number;
  initialSort: string;
  initialLimit: number;
  initialFinancialYear?: number;
  /**
   * Filter tier resolved on the server, when the page knows it.
   *
   * `useSession()` only resolves on the client, so the server-rendered pass
   * always looks like a guest — without this, a subscriber sees "Sign in"
   * chips on every filter for as long as the session request takes. /listen
   * pages omit it deliberately: reading the session there would opt those
   * cacheable pages into dynamic rendering.
   */
  initialTier?: FilterTier;
  /**
   * Filter values to seed the sidebar with when the URL has no corresponding
   * query params. Used by /listen/[slug] landing pages to pre-apply a config-
   * driven filter set without putting params in the canonical URL. URL params
   * (when present) always win over these defaults.
   */
  presetFilters?: Partial<FilterParams>;
  /**
   * Slug of the /listen page rendering this client, when any. Forwarded with
   * every search so the API can re-apply the page's configured preset for
   * filters the visitor's tier cannot otherwise use.
   */
  listSlug?: string;
  /**
   * Content rendered inside the results column, above the results table. On
   * desktop the sidebar stays on the left while this header scrolls together
   * with the result rows. /listen/[slug] landing pages pass their descriptive
   * hero through this slot; /search leaves it undefined.
   */
  resultsHeader?: ReactNode;
}


function filtersToSearchParams(
  filters: FilterParams,
  naceCodes: NaceCode[],
  overrides: {
    sort: string;
    offset: number;
    limit: number;
  },
) {
  return buildBackendFilters({ ...filterParamsToParsed(filters), ...overrides }, { naceCodes });
}

/**
 * Filters → a `/search` URL.
 *
 * Exported because the saved-search cards need the same mapping to open a
 * stored filter set. A second implementation is exactly how the three range
 * tables drifted apart — the param names here (`naceCode` for `naceCodes`,
 * `person_name` for `personName`, …) are not derivable from the field names
 * and must not be re-guessed elsewhere.
 */
export function filtersToUrl(
  filters: FilterParams,
  overrides: {
    sort: string;
    offset: number;
    limit: number;
  },
  locale?: string,
): string {
  const params = new URLSearchParams();
  params.set('offset', overrides.offset.toString());
  params.set('sort', overrides.sort);
  params.set('limit', overrides.limit.toString());
  const year = sanitizeFinancialYear(filters.financialYear);
  if (year !== undefined) params.set('year', String(year));

  if (filters.q) params.set('q', filters.q);
  filters.city.forEach((c) => params.append('city', c));
  if (filters.registerId) params.set('registerId', filters.registerId);
  if (filters.registerCourt) params.set('registerCourt', filters.registerCourt);
  if (filters.registerType) params.set('registerType', filters.registerType);
  if (filters.euid) params.set('euid', filters.euid);
  if (filters.hasYearlyMetadata) params.set('hasYearlyMetadata', 'true');
  filters.Bundesland.forEach((b) => params.append('Bundesland', b));
  for (const metric of ['revenue', 'aktiva', 'profit', 'employees'] as const) {
    for (const side of ['Min', 'Max'] as const) {
      const key = `${metric}${side}` as const;
      if (filters[key] !== undefined) params.set(key, String(filters[key]));
    }
  }
  if (filters.revenueIncludeNull && (filters.revenueMin !== undefined || filters.revenueMax !== undefined)) params.set('revenueIncludeNull', 'true');
  filters.ageRanges.forEach((a) => params.append('ageRange', a));
  filters.naceCodes.forEach((n) => params.append('naceCode', n));
  filters.industries.forEach((i) => params.append('industry', i));
  if (filters.foundedAtFrom) params.set('foundedAtFrom', filters.foundedAtFrom);
  if (filters.foundedAtTo) params.set('foundedAtTo', filters.foundedAtTo);
  filters.tags.forEach((k) => params.append('tag', k));
  if (filters.personName) params.set('person_name', filters.personName);
  filters.rechtsformCodes.forEach((r) => params.append('rechtsformCode', r));
  if (filters.cpvCode) params.set('cpvCode', filters.cpvCode);
  filters.memberships.forEach((m) => params.append('membership', m));
  filters.legalStatus.forEach((v) => params.append('legalStatus', v));
  filters.companySize.forEach((v) => params.append('companySize', v));
  // DE is the default and stays out of the URL; global must be explicit or a
  // reload would silently snap back to Germany.
  if (filters.country !== 'DE') params.set('country', filters.country || 'all');
  filters.tenderRole.forEach((v) => params.append('tenderRole', v));
  // Serialised explicitly as "true"/"false": these are tri-state, so an
  // omitted key and a `false` mean different things and a bare presence
  // flag could not express both.
  if (filters.familyManaged !== undefined)
    params.set('familyManaged', String(filters.familyManaged));
  if (filters.founderLed !== undefined)
    params.set('founderLed', String(filters.founderLed));
  if (filters.successionRisk !== undefined)
    params.set('successionRisk', String(filters.successionRisk));
  if (filters.leiCode) params.set('leiCode', filters.leiCode);
  if (filters.ustIdnr) params.set('ustIdnr', filters.ustIdnr);
  if (filters.hasWebsite) params.set('hasWebsite', 'true');
  if (filters.hasEmail) params.set('hasEmail', 'true');

  const basePath = locale ? localizePathname('/search', locale as Locale) : '/search';
  return `${basePath}?${params.toString()}`;
}

export default function SearchPageClient({
  naceCodes: initialNaceCodes,
  cityList: initialCityList,
  tagList: initialTagList,
  searchResponse: initialSearchResponse,
  searchSkipped,
  initialOffset,
  initialSort,
  initialLimit,
  initialFinancialYear,
  initialTier,
  presetFilters,
  listSlug,
  resultsHeader,
}: SearchPageClientProps) {
  const { t } = useTranslation();
  const urlSearchParams = useSearchParams();
  const renderedSearchParams = searchParamsWithoutPreview(urlSearchParams).toString();
  const searchParams = useMemo(
    () => new URLSearchParams(renderedSearchParams),
    [renderedSearchParams],
  );
  const { previewEuId, openPreview, closePreview } = useCompanyPreview();
  const pathname = usePathname();
  const aiProgress = useAiSearchProgress();
  const aiIsLoading = !listSlug && (aiProgress.phase === 'interpreting' || aiProgress.phase === 'loading');
  // Which rendered search an AI search produced, so its search_query log row
  // can say so (the progress itself is reset as soon as the results render).
  const aiOriginRef = useRef<{ key: string; query: string } | null>(null);
  useEffect(() => {
    const progress = getAiSearchProgress();
    const key = aiSearchKey(new URLSearchParams(renderedSearchParams));
    if (progress.phase === 'loading' && progress.targetKey === key) {
      aiOriginRef.current = { key, query: progress.query };
      resetAiSearchProgress();
    }
  }, [renderedSearchParams]);
  const { locale } = useLocale();
  const { data: session, status: sessionStatus } = useSession();
  const recordRecentSearch = useRecordRecentSearch();
  const isAdminUser = isAdmin(session);
  // Export and enrich are Pro or Admin only. Export is shown to everyone (with
  // a "Pro feature" prompt for the rest); the enrich buttons are hidden for the
  // rest.
  const isProOrAdmin = isAdminUser || isPro(session);
  // Filter gating runs off the server-known tier until the client session
  // lands, so the locks never contradict what the server will actually
  // enforce on the next request.
  const tier: FilterTier =
    sessionStatus === 'loading' && initialTier
      ? initialTier
      : tierForSession(session);

  // Lazy-load filter data if not provided by SSR. Fire three independent
  // fetches so the small NACE list lands in the sidebar immediately
  // (~50ms) instead of being held up behind the keyword scan over
  // yearly_metadata (~3-5s).
  const [naceCodes, setNaceCodes] = useState(initialNaceCodes);
  const [cityList, setCityList] = useState(initialCityList);
  const [tagList, setTagList] = useState(initialTagList);
  const filtersFetchedRef = useRef(false);
  useEffect(() => {
    if (filtersFetchedRef.current) return;
    if (
      initialNaceCodes.length > 0 &&
      initialCityList.length > 0 &&
      initialTagList.length > 0
    )
      return;
    filtersFetchedRef.current = true;
    if (initialNaceCodes.length === 0) {
      fetch('/api/search/filters/nace')
        .then((res) => res.json())
        .then((data) => {
          if (data.naceCodes) setNaceCodes(data.naceCodes);
        })
        .catch(() => {});
    }
    if (initialTagList.length === 0) {
      fetch('/api/search/filters/keywords')
        .then((res) => res.json())
        .then((data) => {
          if (data.tags) setTagList(data.tags);
        })
        .catch(() => {});
    }
  }, [initialNaceCodes, initialCityList, initialTagList]);

  // Default closed so the mobile fullscreen overlay doesn't flash on SSR
  // hydration. Desktop opens it on mount via the effect below.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersInitRef = useRef(false);
  useEffect(() => {
    if (filtersInitRef.current) return;
    filtersInitRef.current = true;
    if (window.innerWidth >= 1024) setFiltersOpen(true);
  }, []);

  // Mobile flow: the header's filter button dispatches
  // `firmendata:open-filters` (see HeaderLogo), which opens the filter panel
  // fullscreen on mobile. The sidebar's own submit handler closes it again.
  useEffect(() => {
    const onOpen = () => setFiltersOpen(true);
    window.addEventListener('firmendata:open-filters', onOpen);
    return () => window.removeEventListener('firmendata:open-filters', onOpen);
  }, []);

  // Lock body scroll while the mobile fullscreen overlay is open so iOS Safari
  // doesn't leak touch scrolling to the page underneath. Desktop is unaffected
  // because the sidebar is in normal flow there.
  useEffect(() => {
    if (!filtersOpen) return;
    if (window.innerWidth >= 1024) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filtersOpen]);

  // Mobile shows results as cards capped at 10 per page. If the initial limit
  // (URL or default) is larger, re-issue the search at 10 once on mount so the
  // pagination steps line up with the visible cards.
  const mobileLimitInitRef = useRef(false);
  useEffect(() => {
    if (mobileLimitInitRef.current) return;
    if (window.innerWidth >= 1024) return;
    mobileLimitInitRef.current = true;
    if (currentLimit > 10) {
      handleLimitChange(10);
    }
    // handleLimitChange is stable per the deps below; we intentionally only
    // want this to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLoading, setIsLoading] = useState(false);

  // Client-side search state (initialized from SSR response)
  const [searchResponse, setSearchResponse] = useState(initialSearchResponse);
  // The search params the response on screen belongs to. The URL changes
  // before a new response arrives (client search replaces it first; a soft
  // navigation re-renders before the reset effect runs), so the search log
  // must wait until both agree — it used to record the previous count.
  const [responseParams, setResponseParams] = useState(renderedSearchParams);
  const [currentOffset, setCurrentOffset] = useState(initialOffset);
  const [currentSort, setCurrentSort] = useState(initialSort);
  const [currentLimit, setCurrentLimit] = useState(initialLimit);
  const [currentFinancialYear, setCurrentFinancialYear] =
    useState(initialFinancialYear);

  // Desktop uses a fixed full-viewport split layout, so we lock body scroll
  // to keep the page from double-scrolling. Mobile uses the natural page flow
  // (the results list scrolls with the page), so we must NOT lock body there
  // or the page becomes unscrollable.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 1024) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const initialFilters: FilterParams = useMemo(() => {
    const preset = normalizeFilterParams(listSlug && pathname === localizePathname('/search', locale as Locale) ? undefined : presetFilters);
    const urlBounds = parseMetricURLBounds(Object.fromEntries([...new Set(searchParams.keys())].map((key) => [key, searchParams.getAll(key)])));
    // URL param wins when present; otherwise fall back to the preset supplied
    // by /listen/[slug] landing pages; otherwise empty default.
    const pickStr = (key: string, presetVal?: string): string =>
      searchParams.get(key) || presetVal || '';
    const pickArr = (key: string, presetVal?: string[]): string[] => {
      const fromUrl = searchParams.getAll(key);
      return fromUrl.length > 0 ? fromUrl : (presetVal ?? []);
    };
    const pickBool = (key: string, presetVal?: boolean): boolean =>
      searchParams.has(key)
        ? searchParams.get(key) === 'true'
        : (presetVal ?? false);

    // Tri-state sibling of pickBool: only the literal "true"/"false" bind,
    // so an absent key stays `undefined` (no filter) rather than collapsing
    // into `false`, which here is a real selection.
    const pickTri = (
      key: string,
      presetVal: boolean | undefined,
    ): boolean | undefined => {
      const raw = searchParams.get(key);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return presetVal;
    };

    return {
      q: pickStr('q', preset.q),
      city: pickArr('city', preset.city),
      registerId: pickStr('registerId', preset.registerId),
      registerCourt: pickStr('registerCourt', preset.registerCourt),
      registerType: pickStr('registerType', preset.registerType),
      euid: pickStr('euid', preset.euid),
      Bundesland: pickArr('Bundesland', preset.Bundesland),
      revenueMin: searchParams.has('revenueMin') || searchParams.has('revenueMax') || searchParams.has('revenueRange') ? urlBounds.revenueMin : preset.revenueMin,
      revenueMax: searchParams.has('revenueMin') || searchParams.has('revenueMax') || searchParams.has('revenueRange') ? urlBounds.revenueMax : preset.revenueMax,
      revenueIncludeNull: pickBool(
        'revenueIncludeNull',
        preset.revenueIncludeNull,
      ),
      aktivaMin: searchParams.has('aktivaMin') || searchParams.has('aktivaMax') || searchParams.has('aktivaRange') ? urlBounds.aktivaMin : preset.aktivaMin,
      aktivaMax: searchParams.has('aktivaMin') || searchParams.has('aktivaMax') || searchParams.has('aktivaRange') ? urlBounds.aktivaMax : preset.aktivaMax,
      financialYear:
        sanitizeFinancialYear(searchParams.get('year')) ??
        sanitizeFinancialYear(preset.financialYear),
      profitMin: searchParams.has('profitMin') || searchParams.has('profitMax') || searchParams.has('profitRange') ? urlBounds.profitMin : preset.profitMin,
      profitMax: searchParams.has('profitMin') || searchParams.has('profitMax') || searchParams.has('profitRange') ? urlBounds.profitMax : preset.profitMax,
      employeesMin: searchParams.has('employeesMin') || searchParams.has('employeesMax') || searchParams.has('employeeRange') ? urlBounds.employeesMin : preset.employeesMin,
      employeesMax: searchParams.has('employeesMin') || searchParams.has('employeesMax') || searchParams.has('employeeRange') ? urlBounds.employeesMax : preset.employeesMax,
      ageRanges: pickArr('ageRange', preset.ageRanges),
      naceCodes: pickArr('naceCode', preset.naceCodes),
      industries: pickArr('industry', preset.industries),
      foundedAtFrom: pickStr('foundedAtFrom', preset.foundedAtFrom),
      foundedAtTo: pickStr('foundedAtTo', preset.foundedAtTo),
      tags: pickArr('tag', preset.tags),
      personName: pickStr('person_name', preset.personName),
      rechtsformCodes: pickArr(
        'rechtsformCode',
        preset.rechtsformCodes,
      ),
      cpvCode: pickStr('cpvCode', preset.cpvCode),
      hasYearlyMetadata: pickBool(
        'hasYearlyMetadata',
        preset.hasYearlyMetadata,
      ),
      memberships: pickArr('membership', preset.memberships),
      legalStatus: pickArr('legalStatus', preset.legalStatus),
      companySize: pickArr('companySize', preset.companySize),
      // Germany is the default; 'all' in the URL is the explicit global.
      country: (() => {
        if (!searchParams.has('country')) return preset.country;
        const c = (searchParams.get('country') || 'DE').toUpperCase();
        return c === 'ALL' ? '' : c === 'CH' ? 'CH' : 'DE';
      })(),
      tenderRole: pickArr('tenderRole', preset.tenderRole),
      familyManaged: pickTri('familyManaged', preset.familyManaged),
      founderLed: pickTri('founderLed', preset.founderLed),
      successionRisk: pickTri('successionRisk', preset.successionRisk),
      leiCode: pickStr('leiCode', preset.leiCode),
      // State holds the digit tail only — the UI shows a fixed "DE" prefix
      // and the backend mapping prepends DE when sending. Strip any leading
      // DE from an external URL so we don't double-prefix.
      ustIdnr: (searchParams.get('ustIdnr') || preset.ustIdnr || '')
        .replace(/^DE/i, '')
        .replace(/\D/g, ''),
      hasWebsite: pickBool('hasWebsite', preset.hasWebsite),
      hasEmail: pickBool('hasEmail', preset.hasEmail),
    };
  }, [searchParams, presetFilters, listSlug, pathname, locale]);

  // Active filter count for the header badge, derived from the filter object
  // rather than a hand-kept list — that list had already drifted from
  // FilterParams. `q` is excluded: it is the search term, shown in the header
  // input, not a badge. Broadcast as a CustomEvent so the global HeaderLogo
  // can listen without importing search-page state.
  const filterCount = useMemo(
    () => activeFilterCount(initialFilters, tier),
    [initialFilters, tier],
  );

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('firmendata:filter-count', { detail: filterCount }),
    );
  }, [filterCount]);

  // Cities are fetched per country and re-fetched when the scope changes, so
  // the dropdown never offers a city the current country cannot return.
  // Unlike the state list (a 16-entry constant the client can filter), there
  // are 13.6K cities and which country each belongs to is only knowable in
  // SQL. Kept out of the one-shot filter block above, which is fire-once by
  // design; declared here because it reads the parsed URL filters.
  const cityCountry = initialFilters.country;
  useEffect(() => {
    if (initialCityList.length > 0) return;
    let cancelled = false;
    const qs = cityCountry ? `?country=${cityCountry}` : '';
    fetch(`/api/search/filters/cities${qs}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.cities) setCityList(data.cities);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cityCountry, initialCityList]);

  // Fetch default results on mount only when SSR deliberately skipped the
  // search. A real search that returned 0 results must NOT fall back to the
  // default list — it should show "no results".
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) return;
    if (searchSkipped) {
      initialFetchDone.current = true;
      setIsLoading(true);
      searchCompanies({ limit: initialLimit, offset: 0, sort: initialSort })
        .then((res) => setSearchResponse(res))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [searchSkipped, initialLimit, initialSort]);

  // Log search query to /api/log/search after the result count settles.
  // Gated on real interaction for guests (mirrors PageViewLogger) so crawlers
  // and link unfurlers — which fetch HTML but don't move a mouse — never log.
  const lastLoggedSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;

    if (!hasAnyFilter(initialFilters, tier)) return;
    if (responseParams !== renderedSearchParams) return;

    const url = `/search?${renderedSearchParams}`;
    if (lastLoggedSearchRef.current === url) return;

    const isGuest = sessionStatus !== 'authenticated';

    const doLog = () => {
      lastLoggedSearchRef.current = url;
      if (sessionStatus === 'authenticated') {
        recordRecentSearch({
          url,
          filters: { ...initialFilters },
          resultCount: searchResponse.total,
        });
      }
      fetch('/api/log/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Every applied filter, derived from the filter object — the old
          // hand-written subset covered 13 of 28, so searches narrowed only
          // by e.g. Rechtsform or membership were invisible to analytics.
          ...activeFilterValues(initialFilters, tier),
          filterKeys: Object.keys(activeFilterValues(initialFilters, tier)),
          sort: currentSort,
          resultCount: searchResponse.total,
          zeroResults: searchResponse.total === 0,
          url,
          ...(aiOriginRef.current?.key ===
            aiSearchKey(new URLSearchParams(renderedSearchParams)) && {
            ai: true,
            aiQuery: aiOriginRef.current.query.slice(0, 200),
          }),
        }),
        keepalive: true,
      }).catch(() => {});
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isGuest) {
      timer = setTimeout(() => {
        if (interactionState.happened) {
          doLog();
        } else {
          let checks = 0;
          interval = setInterval(() => {
            checks++;
            if (interactionState.happened) {
              clearInterval(interval!);
              doLog();
            } else if (checks >= 6) {
              clearInterval(interval!);
            }
          }, 2000);
        }
      }, 3000);
    } else {
      doLog();
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [
    searchParams,
    renderedSearchParams,
    responseParams,
    initialFilters,
    searchResponse.total,
    currentSort,
    sessionStatus,
    tier,
    recordRecentSearch,
  ]);

  // Abort controller for in-flight searches
  const abortRef = useRef<AbortController | null>(null);
  // Filters behind the currently displayed results. executeSearch records the
  // exact FilterParams it ran with, so the Excel export can re-fetch the same
  // result set (just more rows) regardless of how the search was triggered.
  const lastFiltersRef = useRef<FilterParams>(initialFilters);
  // Track whether the first client-side search has happened (for pushState vs replaceState)
  const hasClientSearched = useRef(false);

  // Sync from SSR when URL changes externally (e.g. browser back/forward, or a
  // new search from the header). Skip only for the default search, whose empty
  // response is a placeholder the on-mount client fetch fills in — for a real
  // search we sync even when it returned 0 results, so the previous results
  // clear instead of lingering.
  //
  // Note: returning to the bare default search from an already-open search page
  // (the "Search" nav link, the header search bar submitted empty) is handled
  // by a full page reload — `hardNavigate` on the link, `goToKeywordlessSearch`
  // in LandingSearch — so this effect doesn't need to untangle stale client
  // state from a same-page soft navigation.
  useEffect(() => {
    if (searchSkipped) return;
    setSearchResponse(initialSearchResponse);
    setResponseParams(renderedSearchParams);
    setCurrentOffset(initialOffset);
    setCurrentSort(initialSort);
    setCurrentLimit(initialLimit);
    setCurrentFinancialYear(initialFinancialYear);
    // The results now on screen were produced by the URL's filters, so the
    // export re-fetch must use those — not whatever executeSearch last ran.
    // initialFilters stays out of the deps on purpose: its identity also
    // changes on every shallow pushState executeSearch performs, and firing
    // there would clobber fresh client results with the stale SSR response.
    lastFiltersRef.current = initialFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchSkipped,
    initialSearchResponse,
    initialOffset,
    initialSort,
    initialLimit,
    initialFinancialYear,
  ]);

  // Client-side search: fetch via API route, update URL without full page reload
  const executeSearch = useCallback(
    async (
      filters: FilterParams,
      overrides: {
        sort: string;
        offset: number;
        limit: number;
      },
    ) => {
      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Next patches native history to update useSearchParams, which changes
      // initialFilters and triggers the sidebar sync after a chip removal.
      // Update URL (shallow — no server re-render).
      // Use pushState on first client search so back-button returns to the
      // previous page, then replaceState for subsequent filter changes.
      const url = filtersToUrl(filters, overrides, locale);
      if (!hasClientSearched.current) {
        hasClientSearched.current = true;
        window.history.pushState(null, '', url);
      } else {
        window.history.replaceState(null, '', url);
      }

      // Remember the filters behind this result set for the export re-fetch.
      lastFiltersRef.current = filters;

      setIsLoading(true);
      try {
        const params = filtersToSearchParams(filters, naceCodes, overrides);
        const response = await searchCompanies({
          ...params,
          ...(listSlug && { listSlug, locale }),
        });
        if (!controller.signal.aborted) {
          setSearchResponse(response);
          setResponseParams(
            searchParamsWithoutPreview(
              new URL(url, window.location.origin).searchParams,
            ).toString(),
          );
          setCurrentOffset(overrides.offset);
          setCurrentSort(overrides.sort);
          setCurrentLimit(overrides.limit);
          setCurrentFinancialYear(sanitizeFinancialYear(filters.financialYear));
        }
      } catch (err: unknown) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          console.error('Search failed:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [naceCodes, listSlug, locale],
  );

  // The header's flag selector (LandingSearch) applies its choice here, so a
  // country change runs through the same client search path as any sidebar
  // filter — instant results, shallow URL update, sidebar state synced from
  // the URL. It acts on the filters behind the results on screen; staged
  // sidebar edits stay staged.
  const applyCountry = useCallback(
    (country: string) => {
      const prev = lastFiltersRef.current;
      if ((prev.country ?? 'DE') === country) return;
      executeSearch(
        {
          ...prev,
          country,
          Bundesland: filterLocationsForCountry(prev.Bundesland, country),
          // Cities cannot be checked client-side (13.6K of them, country
          // known only in SQL), so narrowing to a specific country clears
          // them: "Switzerland + Hamburg" is a guaranteed empty result the
          // user did not ask for. Widening to global keeps them — every
          // selected city is still reachable there.
          city: country ? [] : prev.city,
        },
        { sort: currentSort, offset: 0, limit: currentLimit },
      );
    },
    [executeSearch, currentSort, currentLimit],
  );

  useEffect(() => {
    const onSetCountry = (e: Event) =>
      applyCountry((e as CustomEvent<string>).detail ?? 'DE');
    window.addEventListener('firmendata:set-country', onSetCountry);
    return () =>
      window.removeEventListener('firmendata:set-country', onSetCountry);
  }, [applyCountry]);

  const handleSubmit = useCallback(
    (filters: FilterParams) => {
      const filterCount =
        filters.Bundesland.length +
        (filters.revenueMin !== undefined || filters.revenueMax !== undefined ? 1 : 0) +
        (filters.aktivaMin !== undefined || filters.aktivaMax !== undefined ? 1 : 0) +
        (filters.profitMin !== undefined || filters.profitMax !== undefined ? 1 : 0) +
        (filters.employeesMin !== undefined || filters.employeesMax !== undefined ? 1 : 0) +
        filters.ageRanges.length +
        filters.naceCodes.length +
        filters.industries.length +
        filters.tags.length +
        (filters.personName ? 1 : 0) +
        filters.city.length +
        (filters.registerId ? 1 : 0) +
        (filters.registerCourt ? 1 : 0) +
        (filters.registerType ? 1 : 0) +
        (filters.euid ? 1 : 0) +
        (filters.hasYearlyMetadata ? 1 : 0) +
        (filters.foundedAtFrom ? 1 : 0) +
        (filters.foundedAtTo ? 1 : 0) +
        filters.rechtsformCodes.length +
        (filters.cpvCode ? 1 : 0) +
        filters.memberships.length +
        filters.legalStatus.length +
        filters.tenderRole.length +
        (filters.familyManaged !== undefined ? 1 : 0) +
        (filters.founderLed !== undefined ? 1 : 0) +
        (filters.successionRisk !== undefined ? 1 : 0) +
        (filters.leiCode ? 1 : 0) +
        (filters.ustIdnr ? 1 : 0) +
        (filters.hasWebsite ? 1 : 0) +
        (filters.hasEmail ? 1 : 0);
      trackSearchPerformed({
        query: filters.q,
        filterCount,
        resultCount: searchResponse.total,
      });

      const sortOverride =
        (filters.q || filters.personName) &&
        (currentSort === 'revenue-desc' || !searchParams.get('sort'))
          ? 'relevance'
          : currentSort;
      executeSearch(filters, {
        sort: sortOverride,
        offset: 0,
        limit: currentLimit,
      });

      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setFiltersOpen(false);
      }
    },
    [
      executeSearch,
      searchResponse.total,
      currentSort,
      currentLimit,
      searchParams,
    ],
  );

  // ── saved searches ────────────────────────────────────────────────────────
  const { items: savedSearches } = useSavedSearches();
  const markSavedSearchOpened = useMarkOpened();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<FilterParams | null>(null);

  /**
   * Save applies the filters as well.
   *
   * The sidebar hands over its *staged* state, which need not match the
   * results on screen. Running the search as part of saving is what removes
   * the ambiguity — after this the visible results are the saved search,
   * and the dialog's chip preview showed exactly what was stored.
   */
  const handleSaveSearch = useCallback(
    (filters: FilterParams) => {
      handleSubmit(filters);
      setPendingSave(filters);
      setSaveDialogOpen(true);
    },
    [handleSubmit],
  );

  const handleLoadSavedSearch = useCallback(
    (id: number) => {
      const saved = savedSearches.find((s) => s.id === id);
      if (!saved) return;
      // The badge counts new matches since the user last *looked*, so it
      // only resets here — not when the card merely renders.
      markSavedSearchOpened(id);
      const filters = normalizeFilterParams(saved.filters);
      // `sort` holds this page's combined form ("revenue-desc"), the same
      // string executeSearch and the backend both already understand — so
      // sort_direction stays unused here rather than being re-derived.
      executeSearch(filters, {
        sort: normalizeSearchSort(
          saved.sort || currentSort,
          filters.q || filters.personName,
        ),
        offset: 0,
        limit: currentLimit,
      });
    },
    [savedSearches, markSavedSearchOpened, executeSearch, currentSort, currentLimit],
  );

  const savedSearchOptions = useMemo(
    () =>
      savedSearches.map((s) => ({
        id: s.id,
        name: s.name,
        filters: normalizeFilterParams(s.filters),
      })),
    [savedSearches],
  );

  const isCurrentSearchSaved = useMemo(() => {
    const current = canonicalFilters(
      initialFilters as unknown as Record<string, unknown>,
    );
    return savedSearches.some((s) => canonicalFilters(s.filters) === current);
  }, [savedSearches, initialFilters]);

  const pendingSaveQuery = useMemo(() => {
    if (!pendingSave) return null;
    return filtersToSearchParams(pendingSave, naceCodes, {
      sort: currentSort,
      offset: 0,
      limit: currentLimit,
    }) as unknown as Record<string, unknown>;
  }, [pendingSave, naceCodes, currentSort, currentLimit]);

  const handleSortChange = useCallback(
    (newSort: string) => {
      trackSearchSortChange(newSort);
      executeSearch(initialFilters, {
        sort: newSort,
        offset: 0,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentLimit,
    ],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      executeSearch(initialFilters, {
        limit: newLimit,
        offset: 0,
        sort: currentSort,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
    ],
  );

  const handleOffsetChange = useCallback(
    (newOffset: number) => {
      trackSearchPagination(newOffset);
      executeSearch(initialFilters, {
        offset: newOffset,
        sort: currentSort,
        limit: currentLimit,
      });
    },
    [
      executeSearch,
      initialFilters,
      currentSort,
      currentLimit,
    ],
  );

  const handleRefresh = useCallback(() => {
    executeSearch(initialFilters, {
      sort: currentSort,
      offset: currentOffset,
      limit: currentLimit,
    });
  }, [executeSearch, initialFilters, currentSort, currentOffset, currentLimit]);

  // Overlay freshly-enriched employee counts onto the displayed rows. The
  // counts come straight from the enrich response (parsed from employee_data);
  // mv_search is a materialized view we can't write per-click, so this is an
  // in-memory patch. A new search replaces results entirely, falling back to
  // the MV value until the next scheduled MV refresh.
  const handleEmployeeCountsUpdated = useCallback(
    (counts: Record<string, number>) => {
      if (Object.keys(counts).length === 0) return;
      setSearchResponse((prev) => ({
        ...prev,
        results: prev.results.map((r) =>
          counts[r.euId] != null ? { ...r, employees: counts[r.euId] } : r,
        ),
      }));
    },
    [],
  );

  // Fetch up to `limit` rows for the Excel export, reusing the exact filters,
  // sort and financial year behind the currently displayed results. Returns
  // the rows only — it never touches the table state or the URL.
  const handleFetchForExport = useCallback(
    async (limit: number): Promise<CompanySearchResult[]> => {
      const params = filtersToSearchParams(lastFiltersRef.current, naceCodes, {
        sort: currentSort,
        offset: 0,
        limit,
      });
      const response = await searchCompanies(params);
      return response.results;
    },
    [
      naceCodes,
      currentSort,
    ],
  );

  // Log Excel-export usage to /api/log/export so it surfaces on /admin/logs.
  // `stage` is 'click' (button pressed — any user, incl. guests and non-Pro
  // users, before the Pro gate) or 'completed' (file generated). The active
  // filters mirror the search-query log so admins can see what was exported.
  const logExport = useCallback(
    (stage: 'click' | 'completed', extra?: Record<string, unknown>) => {
      const url = `/search?${renderedSearchParams}`;
      const body = {
        stage,
        canExport: isProOrAdmin,
        keyword: searchParams.get('q') || undefined,
        city: searchParams.getAll('city').join(',') || undefined,
        bundesland: searchParams.getAll('Bundesland').join(',') || undefined,
        revenueMin: initialFilters.revenueMin,
        revenueMax: initialFilters.revenueMax,
        aktivaMin: initialFilters.aktivaMin,
        aktivaMax: initialFilters.aktivaMax,
        profitMin: initialFilters.profitMin,
        profitMax: initialFilters.profitMax,
        employeesMin: initialFilters.employeesMin,
        employeesMax: initialFilters.employeesMax,
        naceCodes: searchParams.getAll('naceCode').join(',') || undefined,
        industries: searchParams.getAll('industry').join(',') || undefined,
        tags: searchParams.getAll('tag').join(',') || undefined,
        personName: searchParams.get('person_name') || undefined,
        foundedAtFrom: searchParams.get('foundedAtFrom') || undefined,
        foundedAtTo: searchParams.get('foundedAtTo') || undefined,
        sort: currentSort,
        resultCount: searchResponse.total,
        url,
        ...extra,
      };
      fetch('/api/log/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    },
    [
      searchParams,
      renderedSearchParams,
      isProOrAdmin,
      initialFilters,
      currentSort,
      searchResponse.total,
    ],
  );

  return (
    <div className="search-warm-bg relative lg:fixed lg:left-[var(--app-sidebar-width,0px)] lg:right-0 lg:w-[calc(100%-var(--app-sidebar-width,0px))] lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      {/* The sr-only H1 labels the page for assistive tech on /search. When a
        * `resultsHeader` is provided (e.g. /listen/[slug] passes a visible H1
        * in its hero), we skip it to keep a single H1 per page for SEO. */}
      {resultsHeader == null && (
        <h1 className="sr-only">{t('search.sidebar.title')}</h1>
      )}

      <CompanySearchSidebar
        naceCodes={naceCodes}
        cityList={cityList}
        tagList={tagList}
        onSubmit={handleSubmit}
        sort={currentSort}
        offset={currentOffset}
        limit={currentLimit}
        totalLimit={currentLimit}
        onTotalLimitChange={handleLimitChange}
        showFinancialYear
        total={searchResponse.total}
        onOffsetChange={handleOffsetChange}
        onCountryChange={applyCountry}
        filterParams={initialFilters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        mobileFullscreen
        tier={tier}
        hideCompanySearch
        onSaveSearch={session ? handleSaveSearch : undefined}
        savedSearches={savedSearchOptions}
        onLoadSavedSearch={handleLoadSavedSearch}
        isCurrentSearchSaved={isCurrentSearchSaved}
      />

      {pendingSave && pendingSaveQuery && (
        <SaveSearchDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          filters={pendingSave}
          query={pendingSaveQuery}
          sort={currentSort}
        />
      )}

      <main
        aria-label={t('search.sidebar.title')}
        aria-live="polite"
        className="min-h-[70vh] lg:flex-1 lg:min-h-0 min-w-0 flex flex-col relative lg:overflow-hidden"
      >
        {resultsHeader}
        {aiIsLoading && (
          <div role="progressbar" aria-label={t(aiProgress.phase === 'interpreting' ? 'search.ai.interpreting' : 'search.ai.loadingResults')} className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary/20">
            <div className="h-full w-full bg-primary motion-safe:animate-pulse" />
          </div>
        )}
        {aiIsLoading ? <AiSearchLoading key={aiProgress.startedAt} progress={aiProgress} /> : (
          <>
            {!listSlug && <AiSearchBanner />}
            <ActiveFilterBar filters={initialFilters} tier={tier} onChange={handleSubmit} />
            <CompanyResultsTable
              previewEuId={previewEuId}
              onOpenPreview={openPreview}
              results={searchResponse.results}
              total={searchResponse.total}
              totalCapped={searchResponse.totalCapped ?? false}
              offset={currentOffset}
              limit={currentLimit}
              sort={currentSort}
              onSortChange={handleSortChange}
              onOffsetChange={handleOffsetChange}
              onLimitChange={handleLimitChange}
              onRefresh={handleRefresh}
              financialYear={currentFinancialYear}
              canExport={isProOrAdmin}
              canEnrich={isProOrAdmin}
              // Column customization is free for any logged-in user. Guests see the
              // button but get an "account required" prompt (so the locked state
              // below only ever fires for guests).
              canCustomizeColumns={!!session}
              fetchResultsForExport={handleFetchForExport}
              onExportClick={() => logExport('click')}
              onExportSuccess={(info) => logExport('completed', info)}
              onEmployeeCountsUpdated={handleEmployeeCountsUpdated}
              isLoading={isLoading}
              // '' is the explicit Global scope; the table only needs to
              // know when it is Swiss, to pick that scope's columns.
              country={initialFilters.country || undefined}
            />
          </>
        )}
        <CompanyPreviewPanel
          previewEuId={previewEuId}
          onClose={closePreview}
          results={searchResponse.results}
        />
      </main>
    </div>
  );
}
